import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Badge, Button, Card, StatTile, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useDoctorToday, useOpenEncounter } from '@/features/encounters/api';
import { useQueueActions } from '@/features/queue/api';
import { navCount, useNavCounts } from '@/features/shell/api';
import { klTime } from '@/pages/calendar/time';
import { callNextFailure } from '@/pages/queue/call-next-error';
import { RoomDialog } from '@/pages/queue/RoomDialog';
import styles from './dashboards.module.css';

const FLAG_LABEL = {
  normal: 'Normal',
  low: 'Below',
  high: 'Above',
  abnormal: 'Abnormal',
  critical: 'Critical · held',
} as const;

/**
 * Doctor dashboard (Admin v2 #62): patients today with a seen bar, results to
 * review, Up next (a row opens the consultation), Needs your decision and
 * Your day. RL-1861: My inbox counts the inbox's waiting work (the sidebar
 * badge); the Teleconsults tile returns with the video module.
 */
export function DoctorDashboard() {
  const { t } = useTranslation();
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const { data } = useDoctorToday(!!branch);
  const counts = useNavCounts();
  const open = useOpenEncounter();
  const { callNext } = useQueueActions();
  const navigate = useNavigate();
  const toast = useToast();
  const [calling, setCalling] = useState(false);
  const fail = (err: unknown) =>
    toast.show({ tone: 'error', ...callNextFailure(err, t), error: err });
  const openConsult = (ticketId: string) =>
    open
      .mutateAsync(ticketId)
      .then((e) => navigate(`/consultations/${e.id}`))
      .catch(fail);

  if (!branch)
    return (
      <p className={styles.warnNote}>
        Choose the branch you are working at in the branch switch.
      </p>
    );
  const m = data?.myPatients;
  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile
          highlight
          label="My patients today"
          value={m?.total ?? '—'}
          caption={
            m && (
              <span className={styles.barRow}>
                {m.inRoom} with you now · {m.waiting} waiting for any doctor
                <span
                  className={`${styles.bar} ${styles.barLight}`}
                  role="meter"
                  aria-label="Seen"
                  aria-valuemin={0}
                  aria-valuemax={m.total}
                  aria-valuenow={m.seen}
                >
                  <span
                    style={{
                      width: `${m.total ? (100 * m.seen) / m.total : 0}%`,
                    }}
                  />
                </span>
                Seen {m.seen}/{m.total}
              </span>
            )
          }
        />
        <StatTile
          label="Results to review"
          value={data?.resultsToReview.total ?? '—'}
          caption={
            data
              ? `${data.resultsToReview.criticalHeld} critical held`
              : undefined
          }
        />
        <StatTile
          label="My inbox"
          value={counts.data ? navCount(counts.data, 'inbox') : '—'}
          caption="Refills, questions, reading alerts and stock notices"
        />
      </div>
      <div className={styles.split}>
        <div className={styles.stack}>
          <Card
            title="Waiting for a doctor"
            flush
            actions={
              hasPermission('queue:call') && (
                <Button
                  size="sm"
                  variant="primary"
                  icon={<Check size={14} />}
                  onClick={() => setCalling(true)}
                >
                  Call next
                </Button>
              )
            }
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Patient</th>
                  <th>Arrived</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.upNext.length === 0 && (
                  <tr>
                    <td colSpan={4} className={styles.muted}>
                      Nobody waiting.
                    </td>
                  </tr>
                )}
                {data?.upNext.map((u) => (
                  <tr
                    key={u.ticketId}
                    className={styles.clickRow}
                    tabIndex={0}
                    aria-label={`Open the consultation for ${u.patient.fullName}`}
                    onClick={() => void openConsult(u.ticketId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void openConsult(u.ticketId);
                    }}
                  >
                    <td>{u.number}</td>
                    <td>
                      <strong>{u.patient.fullName}</strong>
                      <br />
                      <span className={styles.muted}>
                        {u.service?.name ?? 'Walk-in'} ·{' '}
                        {u.payer?.name ?? 'Self'}
                      </span>
                    </td>
                    <td>{klTime(u.arrivedAt)}</td>
                    <td>
                      <span className={styles.badges}>
                        <Badge>Waiting</Badge>
                        {!u.vitalsTaken && <Badge>No vitals</Badge>}
                        {u.seeFirst && <Badge tone="danger">See first</Badge>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card
            title="Needs your decision"
            actions={<Link to="/results">Review results</Link>}
          >
            {data?.needsDecision.length === 0 && (
              <p className={styles.muted}>No results waiting.</p>
            )}
            <ul className={styles.list}>
              {data?.needsDecision.map((n, i) => (
                <li key={`${n.labOrderId}-${i}`}>
                  <div>
                    <strong>
                      {n.test}
                      {n.value
                        ? ` · ${n.value}${n.unit ? ` ${n.unit}` : ''}`
                        : ''}
                    </strong>
                    <br />
                    <span className={styles.muted}>
                      {n.patient.fullName}
                      {n.referenceRange ? ` · ref ${n.referenceRange}` : ''}
                    </span>
                  </div>
                  {n.flag && (
                    <Badge
                      tone={
                        n.flag === 'normal'
                          ? 'success'
                          : n.flag === 'critical'
                            ? 'danger'
                            : 'warning'
                      }
                    >
                      {FLAG_LABEL[n.flag]}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </div>
        <Card
          title="Your day"
          actions={<Badge>{`${m?.seen ?? 0} seen`}</Badge>}
        >
          {data?.yourDay.length === 0 && (
            <p className={styles.muted}>Nothing scheduled yet.</p>
          )}
          <ol className={styles.timeline}>
            {data?.yourDay.map((d, i) => (
              <li key={`${d.at}-${i}`} className={styles[d.state]}>
                <span className={styles.dot} aria-hidden="true">
                  {d.state === 'seen' && <Check size={12} />}
                </span>
                <div>
                  <strong>
                    {klTime(d.at)} · {d.patient.fullName}
                  </strong>
                  <br />
                  <span className={styles.muted}>
                    {d.detail}
                    {d.state === 'booked' ? ' · booked' : ''}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>
      <RoomDialog
        open={calling}
        title="Call next"
        branchName={branch.name}
        busy={callNext.isPending}
        onClose={() => setCalling(false)}
        onConfirm={(room) =>
          callNext
            .mutateAsync(room)
            .then((t) => {
              toast.show({
                tone: 'success',
                title: `Number ${t.number} called to ${t.room}`,
              });
              setCalling(false);
            })
            .catch(fail)
        }
      />
    </div>
  );
}
